import {createContext, FC, useContext, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {
  initialQueryState,
  PaginationState,
  QUERIES,
  WithChildren,
  WITPaginationState,
} from '@/_metronic/helpers'
import {BaseResponse} from '@/_metronic/helpers/crud-helper/base-list-response'
import {BaseRequest} from '@/_metronic/helpers/crud-helper/base-request'
import { FilterProductModifierRelation, initialFilterProductRelation, ProductModifierRelation } from './_models'
import { useProductModifierRelationRequest } from './ProductModifierRelationRequestProvider'
import { getProductModifierRelations } from './_requests'

/**
 * Begin: Initial Values
 */
export const initialProductModifierRelationResponse = {refetch: () => {}, isLoading: false, request: initialFilterProductRelation}

/**
 * Begin: Response Context
 *
 * It used for context response wrapper, so the response can
 * called everywhere, as long as it wrapped inside the context
 */
type ProductModifierRelationResponseContextProps = {
  response?: BaseResponse<Array<ProductModifierRelation>> | undefined
  refetch: () => void
  isLoading: boolean
  request: BaseRequest<FilterProductModifierRelation>
}

function createResponseContext<T>(initialState: ProductModifierRelationResponseContextProps) {
  return createContext(initialState)
}

const ProductModifierRelationResponseContext = createResponseContext<ProductModifierRelation>(initialProductModifierRelationResponse)

/**
 * Begin: The Provider
 */
const ProductModifierRelationResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useProductModifierRelationRequest()
  const [request, setRequest] = useState<BaseRequest<FilterProductModifierRelation>>(state)
  const updatedQuery = useMemo(() => state, [state])

  useEffect(() => {
    if (request !== updatedQuery) {
      setRequest(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.PRODUCT_LIST}-${JSON.stringify(request)}`,
    () => {
      return getProductModifierRelations(request)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <ProductModifierRelationResponseContext.Provider value={{isLoading: isFetching, refetch, response, request}}>
      {children}
    </ProductModifierRelationResponseContext.Provider>
  )
}

const useProductModifierRelationResponse = () => useContext(ProductModifierRelationResponseContext)

const useProductModifierRelationResponseData = () => {
  const {response} = useProductModifierRelationResponse()
  if (!response) {
    return []
  }

  return response?.response?.data || []
}

const useProductModifierRelationResponsePagination = () => {
  const defaultPaginationState: WITPaginationState = {
    page: 1,
    pageSize: 10,
    totalCount: 1,
  }

  const {response} = useProductModifierRelationResponse()
  if (!response || !response.response) {
    return defaultPaginationState
  }
  const res = response.response
  return {
    page: res.current_page,
    pageSize: res.limit,
    totalCount: res.total_data,
  } as WITPaginationState
}

const useProductModifierRelationResponseLoading = (): boolean => {
  const {isLoading} = useProductModifierRelationResponse()
  return isLoading
}

export {
  ProductModifierRelationResponseProvider,
  useProductModifierRelationResponse,
  useProductModifierRelationResponseData,
  useProductModifierRelationResponsePagination,
  useProductModifierRelationResponseLoading,
}
